/** One visited CRM page kept for the menu-search “Recent” list. */
export interface RecentPage {
  /** Path only (no query/hash), decoded. */
  url: string;
  /** Breadcrumb leaf-to-root labels without list counts, e.g. `["Finance","Invoices","Acme"]`. */
  labels: string[];
  /** `labels` joined with ` › ` for display / search. */
  breadcrumb: string;
  visitedAt: number;
}

export const RECENT_PAGES_MAX = 50;
export const RECENT_PAGES_STORAGE_PREFIX = 'ewents.crm.recent-pages.v1';

const UNAUTH_PATHS = new Set(['/login', '/forgot-password', '/reset-password']);

/** Strip query/hash and decode so `/a%3Ab` and `/a:b` share a key. */
export function normalizeRecentUrl(url: string): string {
  if (!url) {
    return '';
  }
  const path = url.split('#')[0].split('?')[0];
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

/** Drop list totals appended by layout (`Invoices (128)` → `Invoices`). */
export function stripBreadcrumbCount(label: string): string {
  return label.replace(/ \(\d+\)$/, '');
}

export function formatRecentBreadcrumb(labels: string[]): string {
  return labels.map(stripBreadcrumbCount).filter(Boolean).join(' › ');
}

export function shouldTrackRecentUrl(url: string): boolean {
  const path = normalizeRecentUrl(url);
  if (!path || path === '/') {
    return false;
  }
  return !UNAUTH_PATHS.has(path);
}

/**
 * Upsert by URL (move to front), keep at most `max` entries.
 * Same URL + same breadcrumb already at head → no-op (returns same array ref).
 */
export function pushRecentPage(
  entries: RecentPage[],
  next: Omit<RecentPage, 'breadcrumb'> & { breadcrumb?: string },
  max = RECENT_PAGES_MAX
): RecentPage[] {
  const url = normalizeRecentUrl(next.url);
  if (!url || !shouldTrackRecentUrl(url)) {
    return entries;
  }
  const labels = next.labels.map(stripBreadcrumbCount).filter(Boolean);
  if (labels.length === 0) {
    return entries;
  }
  const breadcrumb = next.breadcrumb ?? formatRecentBreadcrumb(labels);
  const head = entries[0];
  if (head?.url === url && head.breadcrumb === breadcrumb) {
    return entries;
  }
  const rest = entries.filter(e => e.url !== url);
  return [{ url, labels, breadcrumb, visitedAt: next.visitedAt }, ...rest].slice(0, max);
}

/** Filter recent pages by label / breadcrumb / url. Empty query → all (caller may cap). */
export function filterRecentPages(entries: RecentPage[], query: string, limit = 12): RecentPage[] {
  const term = query.trim().toLowerCase();
  const matched = !term
    ? entries
    : entries.filter(item => {
        const haystack = [item.breadcrumb, item.url, ...item.labels].join(' ').toLowerCase();
        return haystack.includes(term);
      });
  return matched.slice(0, limit);
}

export function recentPagesStorageKey(userId: string | null | undefined): string {
  return `${RECENT_PAGES_STORAGE_PREFIX}.${userId || 'anon'}`;
}

export function parseRecentPages(raw: string | null): RecentPage[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter(
        (row): row is RecentPage =>
          !!row &&
          typeof row === 'object' &&
          typeof (row as RecentPage).url === 'string' &&
          typeof (row as RecentPage).breadcrumb === 'string' &&
          Array.isArray((row as RecentPage).labels) &&
          typeof (row as RecentPage).visitedAt === 'number'
      )
      .map(row => ({
        url: normalizeRecentUrl(row.url),
        labels: row.labels.map(String),
        breadcrumb: row.breadcrumb,
        visitedAt: row.visitedAt,
      }))
      .filter(row => shouldTrackRecentUrl(row.url))
      .slice(0, RECENT_PAGES_MAX);
  } catch {
    return [];
  }
}
